import { useState } from 'react';
import { Link, useLocation, useRoute } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ExternalLink, FilePlus2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { ApiError } from '@/services/api';
import PolicyConsentRecords from '@/components/policies/policy-consent-records';
import PolicyDetailsForm from '@/components/policies/policy-details-form';
import PolicyDraftEditor from '@/components/policies/policy-draft-editor';
import PolicyVersions from '@/components/policies/policy-versions';
import {
  formatDateTime, getPolicy, getPolicyOptions, getPolicyVersion, policyKeys, SCOPE_LABELS, type PolicyDetail,
} from '@/lib/policies';

type Seed = { title: string; contentMarkdown: string };

function PageShell({ title, subtitle, children }: { title: string; subtitle?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-start gap-3">
          <Link href="/policies">
            <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" /> Policies</Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
            {subtitle && <div className="text-slate-600 mt-1">{subtitle}</div>}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function PolicyHeaderBadges({ detail }: { detail: PolicyDetail }) {
  const p = detail.policy ?? detail.draft;
  if (!p) return null;
  const linked = p.policyScope === 'feature_key' ? p.featureKeyName ?? p.featureKeyId
    : p.policyScope === 'feature_group' ? p.featureGroupName ?? p.featureGroupId : null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="font-mono text-slate-500">{p.id}</span>
      <Badge variant="outline">{SCOPE_LABELS[p.policyScope]}{linked ? ` · ${linked}` : ''}</Badge>
      {p.isRequired ? <Badge>Required</Badge> : <Badge variant="secondary">Optional</Badge>}
      {detail.policy ? (
        <>
          <Badge variant="outline">v{detail.policy.currentVersion}</Badge>
          <span className="text-slate-500">published {formatDateTime(detail.policy.publishedAt)}</span>
          {detail.policy.publicUrl && (
            <a href={detail.policy.publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline">
              Public page <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </>
      ) : <Badge variant="secondary">Not published</Badge>}
    </div>
  );
}

function NewPolicy() {
  const [, setLocation] = useLocation();
  const options = useQuery({ queryKey: policyKeys.options, queryFn: getPolicyOptions });

  return (
    <PageShell title="New policy" subtitle="Saved as a draft. Nothing is visible in the app until you publish.">
      {options.isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : options.error ? (
        <p className="text-red-600">Could not load features: {(options.error as Error).message}</p>
      ) : options.data && (
        <PolicyDraftEditor
          mode="new"
          policy={null}
          draft={null}
          options={options.data}
          onCreated={id => setLocation(`/policies/edit/${id}`)}
        />
      )}
    </PageShell>
  );
}

function EditPolicy({ policyId }: { policyId: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const detail = useQuery({ queryKey: policyKeys.detail(policyId), queryFn: () => getPolicy(policyId) });
  const options = useQuery({ queryKey: policyKeys.options, queryFn: getPolicyOptions });
  const [tab, setTab] = useState<string | null>(null);
  const [seed, setSeed] = useState<Seed | null>(null);

  const startVersion = useMutation({
    mutationFn: async (): Promise<Seed> => {
      const policy = detail.data?.policy;
      if (!policy) throw new Error('Policy is not published');
      try {
        const current = await getPolicyVersion(policyId, policy.currentVersion);
        return { title: current.title || policy.title, contentMarkdown: current.contentMarkdown };
      } catch (error) {
        // A legacy policy may have no document for its current version: start from blank content.
        if (error instanceof ApiError && error.status === 404) return { title: policy.title, contentMarkdown: '' };
        throw error;
      }
    },
    onSuccess: setSeed,
    onError: (error: Error) => toast({ title: 'Could not load the current version', description: error.message, variant: 'destructive' }),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: policyKeys.detail(policyId) });

  if (detail.isLoading || options.isLoading) {
    return <PageShell title="Loading…"><Loader2 className="h-5 w-5 animate-spin" /></PageShell>;
  }
  if (detail.error || !detail.data) {
    const notFound = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <PageShell title={notFound ? 'Policy not found' : 'Could not load policy'}>
        <p className="text-slate-600">{notFound ? `There is no policy or draft with ID "${policyId}".` : (detail.error as Error)?.message}</p>
      </PageShell>
    );
  }
  if (!options.data) {
    return <PageShell title="Could not load features"><p className="text-red-600">{(options.error as Error)?.message}</p></PageShell>;
  }

  const { policy, draft, versions, issues } = detail.data;
  const title = policy?.title ?? draft?.title ?? policyId;
  // Remount the editor only when a publish moves the base version. Saving a draft
  // must not remount it: that would drop the pending "open review" step.
  const editorKey = policy?.currentVersion ?? 'new';

  const issuesAlert = issues.length > 0 && (
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Data problems with the published policy</AlertTitle>
      <AlertDescription>
        <ul className="list-disc pl-5">{issues.map(i => <li key={i}>{i}</li>)}</ul>
        <p className="mt-2">Publishing a new version writes a consistent version and hash.</p>
      </AlertDescription>
    </Alert>
  );

  if (!policy) {
    return (
      <PageShell title={title} subtitle={<PolicyHeaderBadges detail={detail.data} />}>
        <PolicyDraftEditor
          key={editorKey}
          mode="unpublished"
          policyId={policyId}
          policy={null}
          draft={draft}
          options={options.data}
          onPublished={() => { refresh(); setTab('versions'); }}
          onDiscarded={() => setLocation('/policies')}
        />
      </PageShell>
    );
  }

  const activeTab = tab ?? (draft ? 'draft' : 'versions');

  return (
    <PageShell title={title} subtitle={<PolicyHeaderBadges detail={detail.data} />}>
      {issuesAlert}
      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="draft">{draft ? `Draft ${draft.summary.nextVersion}` : 'New version'}</TabsTrigger>
          <TabsTrigger value="versions">Versions ({versions.length})</TabsTrigger>
          <TabsTrigger value="consent">Consent records</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
        </TabsList>

        <TabsContent value="draft" className="mt-6">
          {draft || seed ? (
            <PolicyDraftEditor
              key={editorKey}
              mode="published"
              policyId={policyId}
              policy={policy}
              draft={draft}
              seed={seed ?? undefined}
              options={options.data}
              onPublished={() => { setSeed(null); refresh(); setTab('versions'); }}
              onDiscarded={() => { setSeed(null); refresh(); }}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Start a new version</CardTitle>
                <CardDescription>
                  Starts a draft from version {policy.currentVersion}. Users keep seeing {policy.currentVersion} until you publish.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => startVersion.mutate()} disabled={startVersion.isPending}>
                  {startVersion.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FilePlus2 className="h-4 w-4 mr-2" />}
                  Start new version
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="versions" className="mt-6">
          <Card><CardContent className="pt-6"><PolicyVersions policyId={policyId} versions={versions} /></CardContent></Card>
        </TabsContent>

        <TabsContent value="consent" className="mt-6">
          <PolicyConsentRecords policyId={policyId} versions={versions} />
        </TabsContent>

        <TabsContent value="details" className="mt-6">
          <PolicyDetailsForm key={policy.currentVersion + (policy.isRequired ? 'r' : 'o')} policy={policy} options={options.data} />
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}

export default function PolicyEditorPage() {
  const [isEditing, params] = useRoute('/policies/edit/:id');
  return isEditing && params?.id ? <EditPolicy key={params.id} policyId={params.id} /> : <NewPolicy />;
}
